import { type CSSProperties, type ReactNode, useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { ApiError } from "../../data/api/client";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

export const ALERT: CSSProperties = { color: "var(--needs-input)" };

/** Unsigned: "$1,234.50", or "$10" with `digits` 0. */
export const usd = (amount: number, digits = 2): string =>
  `$${Math.abs(amount).toLocaleString("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })}`;

/** A whole number within bounds, or null. */
export const wholeNumber = (
  raw: string,
  min: number,
  max: number,
): number | null => {
  const n = Number(raw);
  return raw.trim() !== "" && Number.isInteger(n) && n >= min && n <= max
    ? n
    : null;
};

// One section's in-flight action and the error it left.
export const useAction = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (action: () => Promise<unknown>, fallback: string): void => {
    setBusy(true);
    setError(null);
    void action()
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : fallback),
      )
      .finally(() => setBusy(false));
  };
  return { busy, error, run };
};

export const ErrorRow = ({ message }: { readonly message: string | null }) =>
  message ? (
    <div className={styles.row}>
      <span className={styles.help} role="alert" style={ALERT}>
        {message}
      </span>
    </div>
  ) : null;

export type SettingsRowProps = {
  readonly children: ReactNode;
  readonly help?: string;
  readonly label: string;
};

export const SettingsRow = ({ children, help, label }: SettingsRowProps) => (
  <div className={styles.row}>
    <div style={{ flex: 1 }}>
      <div className={styles.label}>{label}</div>
      {help ? <div className={styles.help}>{help}</div> : null}
    </div>
    {children}
  </div>
);

export type SaveBarProps = {
  readonly dirty: boolean;
  readonly flash: boolean;
  readonly onDiscard: () => void;
  readonly onSave: () => void;
};

export const SaveBar = ({ dirty, flash, onDiscard, onSave }: SaveBarProps) => {
  if (!dirty && !flash) {
    return null;
  }
  return (
    <div className={cx(styles.saveBar, flash && styles.flash, "u-fade-rise")}>
      {flash ? (
        <>
          <Icon.Check size={15} /> <span>Saved</span>
        </>
      ) : (
        <>
          <span className={styles.saveDot} />
          <span>Unsaved changes</span>
          <span className={styles.grow} />
          <Button
            className={styles.saveGhost}
            onClick={onDiscard}
            size="sm"
            variant="ghost"
          >
            Discard
          </Button>
          <Button onClick={onSave} size="sm" variant="accent">
            Save changes
          </Button>
        </>
      )}
    </div>
  );
};
